import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { BlockerStore } from 'shared/ui/Blocker'
import modalStore from 'shared/ui/Modal/store/modalStore.ts'
import { ModalTypeList } from 'shared/ui/Modal/store/types.ts'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import {
  type IBaseSmartCallFlowData,
  type IFullSmartCallFlowData,
} from 'entities/SmartCall/api/types'
import { SmartCallApi } from 'entities/SmartCall/api/SmartCallApi.ts'
import { SmartCallFlowBuilderStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder'

export class SmartCallEditStore {
  smartCallFlowBuilderStore
  workflow: IFullSmartCallFlowData | null = null
  blockerStore: BlockerStore
  inboxWorkflowEnabled = false

  constructor(public readonly entities: IEntitiesStore, private id: number) {
    this.smartCallFlowBuilderStore = new SmartCallFlowBuilderStore(this.entities, this)
    makeAutoObservable(this)

    this._syncBlockerConfig()
    this.blockerStore = new BlockerStore(this, this._blockerConfig)
  }

  private _blockerConfig = {
    title: '',
    primaryText: '',
    primaryAction: () => this.onSave(true),
  }

  init = async () => {
    const inbox = await this.entities.inboxesStore.getById(this.id, true)
    if (inbox) this.smartCallFlowBuilderStore.setName(inbox.name)
    await this.initWorkflow()
  }

  private _syncBlockerConfig = () => {
    this._blockerConfig.title = 'Save call flow before closing?'
    this._blockerConfig.primaryText = this.saveBtnText
  }

  get saveBtnText() {
    return this.isCreate
      ? 'Save as draft'
      : this.workflow?.enabled
      ? 'Publish changes'
      : 'Save changes'
  }

  get isCreate() {
    return !this.workflow
  }

  get inbox() {
    const inbox = this.entities.inboxesStore.getItem(this.id)
    if (inbox instanceof Inbox) {
      return inbox
    }
    return null
  }

  get params(): IBaseSmartCallFlowData | null {
    return this.smartCallFlowBuilderStore.params
  }

  get hasInboxWorkflowEnabled() {
    return this.inboxWorkflowEnabled
  }

  get hasEnabled() {
    return this.workflow?.enabled
  }

  get hasActiveCallFlow() {
    return this.hasInboxWorkflowEnabled && this.hasEnabled
  }

  get hasDraft() {
    return Boolean(!this.hasEnabled && this.workflow)
  }

  get isInvalid() {
    return this.workflow?.is_invalid ?? false
  }

  get isSaveButtonDisabled() {
    if (this.isCreate) return false

    return !this.blockerStore.isExistChanges
  }

  onActivateInboxWorkflow = async (status: boolean) => {
    if (this.workflow) {
      if (this.hasActiveCallFlow) {
        await this._onShowDisableFlowModal()

        return
      }

      await this.updateInboxWorkflow({ id: this.workflow.id, status })
    } else {
      const prevState = this.inboxWorkflowEnabled
      this.inboxWorkflowEnabled = status
      if (status) {
        this.smartCallFlowBuilderStore.buildDefaultWorkflow()
      }
      await this.onSave()
      if (!this.workflow) {
        this.inboxWorkflowEnabled = prevState
      }
    }
  }

  onActivateWorkFlow = async () => {
    const isValid = this.smartCallFlowBuilderStore.validateForPublish()
    if (!isValid) {
      toastStore.add({
        type: 'error',
        title: 'Failed to activate',
        desc: 'Complete all required fields',
      })

      return
    }

    await this._onShowActivateFlowModal()
  }

  update = (data: IFullSmartCallFlowData) => {
    this.blockerStore.updateCash()
    this.workflow = data
    this._syncBlockerConfig()
  }

  initWorkflow = async () => {
    try {
      const res = await SmartCallApi.getInboxWorkflow(this.id)

      if (res) {
        runInAction(() => {
          this.inboxWorkflowEnabled = res.data.data.active
          this.workflow = res.data.data.workflow
          if (res.data.data.workflow) {
            this.smartCallFlowBuilderStore.loadFromWorkflow(res.data.data.workflow)
          }
        })
      }
    } catch (e) {
      logger.error(e)
    } finally {
      this._syncBlockerConfig()
      this.blockerStore.updateCash()
    }
  }

  updateInboxWorkflow = async ({ id, status }: { id: string; status: boolean }) => {
    try {
      const res = await SmartCallApi.updateInboxWorkflow(this.id, {
        workflow_id: id,
        active: status,
      })

      runInAction(() => {
        this.inboxWorkflowEnabled = res.data.data.active
      })
    } catch (e) {
      if (e instanceof AxiosError) {
        this.smartCallFlowBuilderStore.setError(e.errors)
      }
      logger.error(e)
    }
  }

  private _onShowDisableFlowModal = async () => {
    const modalId = nanoid()
    modalStore.addModal({
      disabledOnAllClose: true,
      id: modalId,
      type: ModalTypeList.INFO,
      title: 'Disable this flow?',
      desc: 'This will turn off your smart call flow. Incoming calls will no longer follow the automated steps you’ve set up',
      primaryAction: {
        text: 'Yes, disable',
        onAction: async () => {
          if (this.workflow) await this.updateInboxWorkflow({ id: this.workflow.id, status: false })

          modalStore.removeModal(modalId)
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: async () => {
          modalStore.removeModal(modalId)
        },
      },
    })
  }

  private _onShowActivateFlowModal = async () => {
    const modalId = nanoid()
    modalStore.addModal({
      disabledOnAllClose: true,
      id: modalId,
      type: ModalTypeList.SUCCESS,
      title: 'Activate this flow?',
      desc: 'Your call flow is ready. Do you want to activate it now?',
      primaryAction: {
        text: 'Yes, activate',
        onAction: async () => {
          try {
            if (!this.workflow || this.blockerStore.isExistChanges) {
              await this.onSave(true)
            }

            if (!this.workflow) {
              modalStore.removeModal(modalId)
              return
            }

            await SmartCallApi.enableWorkflow(this.workflow.id)
            this.update({ ...this.workflow, enabled: true })

            if (!this.inboxWorkflowEnabled) {
              await this.updateInboxWorkflow({ id: this.workflow.id, status: true })
            }

            toastStore.add({
              type: 'success',
              title: 'Call flow activated',
            })

            modalStore.removeModal(modalId)
          } catch (e) {
            logger.error(e)

            toastStore.add({
              type: 'error',
              title: 'Something went wrong. Please try again',
            })
          }
        },
      },
      secondaryAction: {
        text: 'No, keep editing',
        onAction: async () => {
          modalStore.removeModal(modalId)
        },
      },
    })
  }

  private _onShowPublishChangesModal = () => {
    return new Promise<void>((resolve, reject) => {
      const modalId = nanoid()
      modalStore.addModal({
        disabledOnAllClose: true,
        id: modalId,
        type: ModalTypeList.INFO,
        title: 'Publish changes',
        desc: 'The changes will be applied to your active Call Flow',
        primaryAction: {
          text: 'Publish changes',
          onAction: async () => {
            try {
              if (this.workflow && this.params) {
                const res = await SmartCallApi.updateWorkflow(this.workflow.id, this.params)
                this.update(res.data.payload)

                if (!this.inboxWorkflowEnabled) {
                  await this.updateInboxWorkflow({ id: this.workflow.id, status: true })
                }

                toastStore.add({
                  type: 'success',
                  title: 'Changes published',
                })
              }

              modalStore.removeModal(modalId)
              resolve()
            } catch (e) {
              logger.error(e)
              if (e instanceof AxiosError) {
                toastStore.add({
                  type: 'error',
                  title: e.errors.first,
                })
              }
              reject(e)
            }
          },
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: async () => {
            modalStore.removeModal(modalId)
            reject(new Error('cancelled'))
          },
        },
      })
    })
  }

  onSave = async (withError?: boolean) => {
    if (!this.params) {
      if (withError) throw new Error('No params')
      return
    }
    const isValid = this.smartCallFlowBuilderStore.validateForPublish()
    if (!isValid) {
      toastStore.add({
        type: 'error',
        title: this.hasEnabled ? 'Failed to publish changes' : 'Failed to save',
        desc: 'Complete all required fields',
      })

      if (withError) throw new Error('Validation failed')
      return
    }

    try {
      if (this.workflow) {
        if (this.hasEnabled) {
          await this._onShowPublishChangesModal()

          return
        }

        const res = await SmartCallApi.updateWorkflow(this.workflow.id, this.params)
        toastStore.add({
          type: 'info',
          title: 'Changes saved',
        })
        this.update(res.data.payload)
      } else {
        const res = await SmartCallApi.createWorkflow(this.params)

        const { id } = res.data.payload
        await SmartCallApi.createInboxWorkflow(this.id, {
          workflow_id: id,
          active: this.inboxWorkflowEnabled,
        })

        toastStore.add({
          type: 'info',
          title: 'Call flow saved',
        })

        this.update(res.data.payload)
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        this.smartCallFlowBuilderStore.setError(e.errors)
      }
      logger.error(e)

      if (withError) {
        throw e
      }
    }
  }
}
